import { useCallback, useEffect, useState } from "react";
import { Alert, Col, Form, Row } from "react-bootstrap";
import { useNavigate, useParams } from "react-router-dom";

import Button from "../components/layout/Button";
import LoadingSpinner from "../components/layout/LoadingSpinner";
import BackToUsersButton from "../components/userManagement/backToUsersButton";
import ConfirmActionModal from "../components/userManagement/confirmActionModal";
import { getMostSeniorGroup } from "../components/userManagement/groupOrder";
import GroupPicker from "../components/userManagement/groupPicker";
import { validateUserFields } from "../components/userManagement/userValidation";
import { useCurrentUsername } from "../hooks/authHooks";
import { useUnsavedChangesWarning } from "../hooks/useUnsavedChangesWarning";
import {
  batchUpdateMemberships,
  deleteUser,
  getUser,
  getUserGroups,
  resetPassword,
  updateUser,
} from "../services/accountManagement.service";
import { isHttpError } from "../services/apiService";
import { MembershipChange } from "../types/accountManagement.types";
import { isSameUser } from "../utils/isSameUser";

const sameGroups = (a: string[], b: string[]) =>
  a.length === b.length && a.every((group) => b.includes(group));

const EditUser: React.FC = () => {
  const { username } = useParams<{ username: string }>();
  const navigate = useNavigate();
  const currentUsername = useCurrentUsername();

  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState<string | null>(null);

  const [email, setEmail] = useState("");
  const [givenName, setGivenName] = useState("");
  const [familyName, setFamilyName] = useState("");
  const [groups, setGroups] = useState<string[]>([]);
  const [originalGroups, setOriginalGroups] = useState<string[]>([]);
  // What the server last confirmed, for spotting unsaved edits.
  const [savedProfile, setSavedProfile] = useState({ email: "", givenName: "", familyName: "" });

  const [submitting, setSubmitting] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);

  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [deleteError, setDeleteError] = useState<string | null>(null);

  const [showResetConfirm, setShowResetConfirm] = useState(false);
  const [resetting, setResetting] = useState(false);
  const [resetError, setResetError] = useState<string | null>(null);
  const [resetMessage, setResetMessage] = useState<string | null>(null);

  const fullName = `${givenName.trim()} ${familyName.trim()}`.trim();

  const isOwnAccount = isSameUser(username, currentUsername);
  const isDirty =
    email.trim() !== savedProfile.email.trim() ||
    givenName.trim() !== savedProfile.givenName.trim() ||
    familyName.trim() !== savedProfile.familyName.trim() ||
    !sameGroups(groups, originalGroups);
  useUnsavedChangesWarning(isDirty);
  const protectedGroup = isOwnAccount ? getMostSeniorGroup(originalGroups) : null;

  const loadUser = useCallback(async () => {
    if (!username) return;
    setLoading(true);
    setLoadError(null);
    try {
      const [user, userGroups] = await Promise.all([getUser(username), getUserGroups(username)]);
      setEmail(user.email ?? "");
      setGivenName(user.given_name ?? "");
      setFamilyName(user.family_name ?? "");
      setSavedProfile({
        email: user.email ?? "",
        givenName: user.given_name ?? "",
        familyName: user.family_name ?? "",
      });
      setGroups(userGroups.groups);
      setOriginalGroups(userGroups.groups);
    } catch {
      setLoadError("Unable to load user.");
    } finally {
      setLoading(false);
    }
  }, [username]);

  useEffect(() => {
    void loadUser();
  }, [loadUser]);

  // Re-reads the real memberships after a failed group update so the checkboxes and the next
  // save's diff reflect the server, not what was attempted. A failed refresh keeps what we have.
  const refreshGroups = async (name: string) => {
    try {
      const refreshed = await getUserGroups(name);
      setGroups(refreshed.groups);
      setOriginalGroups(refreshed.groups);
    } catch {
      // Nothing more to do - the save error is already showing.
    }
  };

  const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault();
    if (!username) return;

    setErrorMessage(null);
    setSuccessMessage(null);

    const validationError = validateUserFields({ email, givenName, familyName });
    if (validationError) {
      setErrorMessage(validationError);
      return;
    }

    setSubmitting(true);

    const profile = {
      email: email.trim(),
      given_name: givenName.trim(),
      family_name: familyName.trim(),
      name: fullName,
    };

    try {
      await updateUser(username, profile);
    } catch (error) {
      setErrorMessage(
        isHttpError(error) ? `Update failed (${String(error.response.status)}).` : "Update failed.",
      );
      setSubmitting(false);
      return;
    }

    // The profile is saved from here on, so the typed values are the new baseline even if the
    // group changes below fail - the user's edits must survive a partial failure.
    setSavedProfile({
      email: profile.email,
      givenName: profile.given_name,
      familyName: profile.family_name,
    });

    const toAdd = groups.filter((g) => !originalGroups.includes(g));
    const toRemove = originalGroups.filter((g) => !groups.includes(g));
    const changes: MembershipChange[] = [
      ...toAdd.map((group_name) => ({ username, group_name, action: "add" as const })),
      ...toRemove.map((group_name) => ({ username, group_name, action: "remove" as const })),
    ];

    try {
      if (changes.length > 0) {
        const { results } = await batchUpdateMemberships(changes);
        const failed = results.filter((r) => !r.success);
        if (failed.length > 0) {
          setErrorMessage(
            `Some group changes failed: ${failed.map((f) => `${f.action} ${f.group_name} (${f.error ?? "unknown error"})`).join(", ")}`,
          );
          await refreshGroups(username);
          return;
        }
      }

      setOriginalGroups(groups);
      setSuccessMessage("User updated.");
    } catch (error) {
      setErrorMessage(
        `Profile saved, but group changes failed${
          isHttpError(error) ? ` (${String(error.response.status)})` : ""
        }.`,
      );
      await refreshGroups(username);
    } finally {
      setSubmitting(false);
    }
  };

  const handleConfirmDelete = async () => {
    if (!username || isOwnAccount) return;
    setDeleting(true);
    setDeleteError(null);
    try {
      await deleteUser(username);
      navigate("/admin/users");
    } catch (error) {
      setDeleteError(
        isHttpError(error) ? `Delete failed (${String(error.response.status)}).` : "Delete failed.",
      );
    } finally {
      setDeleting(false);
    }
  };

  const handleConfirmReset = async () => {
    if (!username) return;
    setResetting(true);
    setResetError(null);
    try {
      const { message } = await resetPassword(username);
      setResetMessage(message);
      setShowResetConfirm(false);
    } catch (error) {
      setResetError(
        isHttpError(error) ? `Reset failed (${String(error.response.status)}).` : "Reset failed.",
      );
    } finally {
      setResetting(false);
    }
  };

  if (loading) return <LoadingSpinner message="Loading user..." size="medium" />;
  if (loadError) return <Alert variant="danger">{loadError}</Alert>;

  return (
    <div>
      <div className="d-flex justify-content-between align-items-center mb-3">
        <div className="d-flex align-items-center gap-3">
          <BackToUsersButton confirmLeave={isDirty} />
          <h1 className="mb-0">Edit User</h1>
        </div>
        <div className="d-flex gap-2">
          <button className="btn" type="submit" form="edit-user-form" disabled={submitting}>
            {submitting ? "Saving…" : "Save Changes"}
          </button>
          <Button
            type="button"
            onClick={() => {
              setResetError(null);
              setResetMessage(null);
              setShowResetConfirm(true);
            }}
          >
            Reset Password
          </Button>
          <button
            className="btn btn-danger"
            type="button"
            disabled={isOwnAccount}
            title={isOwnAccount ? "You cannot delete your own account" : undefined}
            onClick={() => {
              setDeleteError(null);
              setShowDeleteConfirm(true);
            }}
          >
            Delete User
          </button>
        </div>
      </div>

      {resetMessage && <Alert variant="success">{resetMessage}</Alert>}

      <Form id="edit-user-form" onSubmit={(e) => void handleSubmit(e)}>
        {errorMessage && <Alert variant="danger">{errorMessage}</Alert>}
        {successMessage && <Alert variant="success">{successMessage}</Alert>}

        <Row>
          <Col md={6}>
            <Form.Group className="mb-3" controlId="edit-user-email">
              <Form.Label>Email</Form.Label>
              <Form.Control
                type="email"
                value={email}
                onChange={(e) => {
                  setEmail(e.target.value);
                }}
              />
            </Form.Group>

            <Form.Group className="mb-3" controlId="edit-user-given-name">
              <Form.Label>First Name</Form.Label>
              <Form.Control
                type="text"
                value={givenName}
                onChange={(e) => {
                  setGivenName(e.target.value);
                }}
              />
            </Form.Group>

            <Form.Group className="mb-3" controlId="edit-user-family-name">
              <Form.Label>Last Name</Form.Label>
              <Form.Control
                type="text"
                value={familyName}
                onChange={(e) => {
                  setFamilyName(e.target.value);
                }}
              />
            </Form.Group>

            <Form.Group className="mb-3" controlId="edit-user-name">
              <Form.Label>Full Name</Form.Label>
              <Form.Control type="text" value={fullName} disabled readOnly />
            </Form.Group>
          </Col>

          <Col md={6}>
            <Form.Group className="mb-3">
              <Form.Label>Access Groups</Form.Label>
              <GroupPicker
                selected={groups}
                onChange={setGroups}
                disabled={submitting}
                lockedGroups={protectedGroup ? [protectedGroup] : []}
                lockedGroupReason="You can't remove your own most senior access group."
              />
            </Form.Group>
          </Col>
        </Row>
      </Form>

      <ConfirmActionModal
        show={showDeleteConfirm}
        title="Delete User"
        body={
          <>
            <p>
              Are you sure you want to delete <strong>{username}</strong>? This cannot be undone.
            </p>
            {deleteError && <Alert variant="danger">{deleteError}</Alert>}
          </>
        }
        confirmLabel="Delete"
        confirmVariant="danger"
        requireTypedConfirmation="confirm delete"
        busy={deleting}
        onConfirm={() => void handleConfirmDelete()}
        onCancel={() => {
          setShowDeleteConfirm(false);
        }}
      />

      <ConfirmActionModal
        show={showResetConfirm}
        title="Reset Password"
        body={
          <>
            <p>
              This will email <strong>{username}</strong> a verification code to reset their
              password. There is no in-app step to complete the reset — the user must finish it via
              the emailed code.
            </p>
            {resetError && <Alert variant="danger">{resetError}</Alert>}
          </>
        }
        confirmLabel="Send Reset Email"
        confirmVariant="primary"
        busy={resetting}
        onConfirm={() => void handleConfirmReset()}
        onCancel={() => {
          setShowResetConfirm(false);
        }}
      />
    </div>
  );
};

export default EditUser;
